export type ReportType = 'bug' | 'feature';

/** Mirrors BugsFeature::REPORT_TYPE_LABELS. */
export const REPORT_TYPE_OPTIONS: { id: ReportType; label: string }[] = [
  { id: 'bug', label: 'Bug' },
  { id: 'feature', label: 'Feature Request' },
];

/** Mirrors BugsFeature::SEVERITIES keys; Jira's option ids stay server-side. */
export type Severity = 'sev_1' | 'sev_2' | 'sev_3';

/**
 * Jira's own labels are full sentences, so the short label is what the UI shows
 * and the sentence becomes the explanation beside it. Kept in step with
 * BugsFeature::SEVERITIES.
 */
export const SEVERITY_OPTIONS: {
  id: Severity;
  label: string;
  description: string;
}[] = [
  {
    id: 'sev_1',
    label: 'Sev-1',
    description:
      'Complete business outage or functionality unavailable with no viable workaround.',
  },
  {
    id: 'sev_2',
    label: 'Sev-2',
    description:
      'Significant business disruption, but a workaround is available, or the issue is localized.',
  },
  {
    id: 'sev_3',
    label: 'Sev-3',
    description:
      'Non-critical issues with intermittent impact or localized slowness, causing minor user inconvenience.',
  },
];

/**
 * Where the local record stands in its handoff to Jira. Distinct from the Jira
 * workflow status (`jiraStatus`), which Jira owns once the ticket exists.
 */
export type SyncState = 'pending' | 'synced' | 'failed';

// The index signature satisfies DataTable's `Record<string, unknown>` constraint
// (the same reason LexiconTermRow carries one) — without it, DataTable<BugsFeatureRow>
// does not typecheck.
export interface BugsFeatureRow {
  [key: string]: unknown;
  id: number;
  reportType: ReportType;
  /** Server-rendered, so the Type column and the type filter cannot disagree. */
  reportTypeLabel: string | null;
  title: string;
  /**
   * Only on the detail payload. The listing has no description column, and sending one meant
   * an unbounded body on every row of every page — see bugs_features/_shared_fields.
   */
  description?: string;
  /**
   * Detail payload only. The accounts `description` already tags, with the ids the readable
   * text no longer carries.
   *
   * `description` above is the display projection — markers stripped — so an editor seeded
   * from it has lost every id by the time it saves. These travel alongside so the save can
   * put them back, in the same shape the composer sends.
   */
  descriptionMentions?: { name: string; userId: number }[];
  reportedBy: string | null;
  jiraIssueKey: string | null;
  jiraUrl: string | null;
  jiraStatus: string | null;
  /** The team triage assigned in Jira. Null until someone does — Insights never sets it. */
  jiraTeamId: string | null;
  jiraTeamName: string | null;
  /**
   * Jira's own copies, deliberately not merged with the local resolution fields.
   *
   * Triage can resolve a ticket while the Insights report is still open, so these answer
   * "what does Jira say" rather than "what is the outcome" — and a reader needs to be able
   * to tell which system decided what.
   */
  jiraResolution: string | null;
  jiraRcaCategory: string | null;
  jiraRootCause: string | null;
  /** Jira's "Planned end" (customfield_10181). Set by triage, never by Insights. */
  jiraPlannedEnd: string | null;
  /**
   * An Idea's own fields on IPT, both null on a bug.
   *
   * `jiraQuarter` is "Quarter." (customfield_11505 — the trailing stop is part of its name)
   * and stands in for a delivery date, which IPT does not have.
   */
  jiraQuarter: string | null;
  jiraProduct: string | null;
  /** Current value — Jira's after triage, the reporter's before. */
  severity: Severity | null;
  severityLabel: string | null;
  severityDescription: string | null;
  syncState: SyncState;
  /** Whether the viewer may correct the report's own text — the reporter, or a triager. */
  canEditDescription?: boolean;
  syncError: string | null;
  /** Page URL, user agent and app version captured at submit time. */
  context: Record<string, unknown> | null;
  createdAt: string | null;
  /** Moved by triage, Jira syncs and resolutions — not just by editing the report. */
  updatedAt: string | null;
  /**
   * Whether this viewer is in the resolver group.
   *
   * Server-computed from the policy rather than inferred here, and it gates the Jira field
   * editor as well as the resolution panel — both are triage decisions, so both ask the
   * same question rather than two that could drift apart.
   */
  canResolve: boolean;
}

export interface BugsFeatureAttachment {
  id: number;
  filename: string;
  contentType: string;
  byteSize: number;
  isImage: boolean;
  isVideo: boolean;
  /** Active Storage redirect URL — stays behind app authentication. */
  url: string;
}

export interface ReporterFacet {
  /** Identity for selection state only — filter on `ids`. */
  id: number;
  /**
   * Every user id this option stands for. Same-named accounts are collapsed into one
   * option server-side, so one person with two logins is one checkbox that returns
   * everything they filed under either.
   */
  ids: number[];
  label: string;
}

/**
 * Options for the sidebar multi-selects. Computed from the unfiltered scope, so
 * choosing one reporter does not remove every other reporter from the list you
 * are choosing from — and narrowing to Bugs does not empty the Feature Requests
 * checkbox you would use to undo it.
 */
export interface BugsFeaturesFacets {
  /** Fixed list from the server, not the values currently in use. */
  reportTypes: { id: ReportType; label: string }[];
  reporters: ReporterFacet[];
  jiraStatuses: string[];
  /**
   * Teams present in the data, plus an `unassigned` sentinel for reports nobody has
   * triaged. Values rather than a fixed list — there are dozens of teams in Jira and the
   * ones that never touched a report are noise in a filter.
   */
  teams: TeamFacet[];
  syncStates: SyncState[];
  /**
   * Products named on at least one report, flattened out of Jira's multi-select. Values
   * rather than a fixed list, for the same reason as teams: the options live in Jira and
   * are added there without a deploy here.
   */
  products: string[];
  /** Fixed list from the server, not the values currently in use. */
  severities: { id: Severity; label: string }[];
}

/** `id` is a Jira team UUID, or UNASSIGNED_TEAM_ID for the untriaged bucket. */
export interface TeamFacet {
  id: string;
  label: string;
}

/** Matches Query::BugsFeatures::UNASSIGNED_TEAM_ID — a NULL cannot travel in a list. */
export const UNASSIGNED_TEAM_ID = 'unassigned';

/**
 * The listing sends snake_case inside the `query` wrapper, matching
 * use-insights-lexicon. sort_attribute stays camelCase-free because the backend
 * maps it straight to a column name.
 */

/**
 * The standard envelope every /queries/* endpoint returns
 * (total_count / page / page_size / results, camelized by Jbuilder.key_format).
 */
export interface BugsFeaturesQueryResponse {
  results: BugsFeatureRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  facets: BugsFeaturesFacets;
}

// Everyone's reports are always listed; narrowing to one person is what the
// "Reported By" filter is for, so a separate mine/all mode would be a second way
// to express the same thing.
/** Open or resolved as recorded in Insights; '' means either. */
export type ResolutionState = '' | 'open' | 'resolved';

export interface BugsFeaturesFilterState {
  searchText: string;
  /** Empty means both — bugs and feature requests share one table. */
  reportTypes: ReportType[];
  reporterIds: number[];
  resolutionState: ResolutionState;
  jiraStatuses: string[];
  teamIds: string[];
  syncStates: SyncState[];
  products: string[];
  severities: Severity[];
  createdAtStart: string;
  createdAtEnd: string;
}

export const DEFAULT_FILTER_STATE: BugsFeaturesFilterState = {
  searchText: '',
  reportTypes: [],
  reporterIds: [],
  resolutionState: '',
  jiraStatuses: [],
  teamIds: [],
  syncStates: [],
  products: [],
  severities: [],
  createdAtStart: '',
  createdAtEnd: '',
};

/**
 * Sidebar filters only — search lives in the toolbar.
 *
 * @param {BugsFeaturesFilterState} f - Current filter selections.
 * @return {number} How many sidebar filters are set.
 */
export function countActiveFilters(f: BugsFeaturesFilterState): number {
  let n = 0;
  if (f.reportTypes.length) n++;
  if (f.reporterIds.length) n++;
  if (f.resolutionState) n++;
  if (f.jiraStatuses.length) n++;
  if (f.teamIds.length) n++;
  if (f.syncStates.length) n++;
  if (f.products.length) n++;
  if (f.severities.length) n++;
  if (f.createdAtStart || f.createdAtEnd) n++;
  return n;
}

export interface NewBugsFeature {
  reportType: ReportType;
  title: string;
  description: string;
  /** Bugs only — feature requests carry no severity. */
  severity?: Severity | null;
  /**
   * Present only when the form was opened from the error page. Recorded as context
   * alongside page_url rather than in the description, so the reporter's own words stay
   * theirs and the trace cannot be edited away before it reaches an engineer.
   */
  crash?: { message: string; stack?: string } | null;
  /**
   * Anyone tagged in the description, and the account each name resolved to.
   *
   * The description carries readable names; the ids travel here because they are what decide
   * who is notified and who Jira tags, and nobody should have to read them while typing.
   */
  mentions?: { name: string; userId: number }[];
}
